import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title="Privacy Policy | Infinite Reyn Productions"
        description="Privacy Policy for Infinite Reyn Productions. Learn how we collect, use, and protect your personal information when you visit our website or sign up for updates."
        canonical="/privacy-policy"
        keywords="privacy policy, data protection, user privacy, Infinite Reyn Productions privacy"
        noindex={false}
      />

      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-50 flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm tracking-wider">BACK</span>
      </button>

      {/* Navigation Hint */}
      <div className="fixed top-8 right-8 z-50">
        <span className="text-xs tracking-[0.3em] text-gold">PRIVACY POLICY</span>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-32">
        {/* Header */}
        <div className="mb-16 text-center">
          <h1 className="text-5xl md:text-6xl font-light mb-6 tracking-wide">
            Privacy Policy
          </h1>
          <div className="w-24 h-px bg-gold mx-auto mb-8"></div>
          <p className="text-sm text-white/60 tracking-wider">
            Last Updated: January 1, 2026
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-lg max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-white/80 leading-relaxed mb-6">
              At Infinite Reyn Productions ("we," "us," or "our"), we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or interact with our services.
            </p>
            <p className="text-white/80 leading-relaxed">
              By using our website, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access the site.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              Information We Collect
            </h2>
            
            <h3 className="text-xl font-light mb-4 text-gold">Personal Information</h3>
            <p className="text-white/80 leading-relaxed mb-6">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed mb-6 space-y-2">
              <li>Submit our contact form</li>
              <li>Sign up for early access to upcoming projects</li>
              <li>Subscribe to email updates or newsletters</li>
              <li>Participate in surveys or promotions</li>
            </ul>
            <p className="text-white/80 leading-relaxed mb-6">
              This information may include:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed mb-6 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Subject and message content (from contact forms)</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3 className="text-xl font-light mb-4 text-gold">Automatically Collected Information</h3>
            <p className="text-white/80 leading-relaxed mb-6">
              When you visit our website, we may automatically collect certain information about your device and browsing actions, including:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed mb-6 space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Referring website</li>
              <li>Pages visited and time spent on pages</li>
              <li>Device identifiers</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              How We Use Your Information
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed space-y-3">
              <li><strong className="text-white">Communication:</strong> To respond to your inquiries, comments, or questions submitted through our contact form</li>
              <li><strong className="text-white">Updates:</strong> To send you information about our projects, releases, and events (only if you've opted in)</li>
              <li><strong className="text-white">Early Access:</strong> To notify you when projects you've expressed interest in are ready to launch</li>
              <li><strong className="text-white">Analytics:</strong> To understand how visitors use our website and improve user experience</li>
              <li><strong className="text-white">Legal Compliance:</strong> To comply with applicable laws and regulations</li>
              <li><strong className="text-white">Protection:</strong> To protect our rights, privacy, safety, or property</li>
            </ul>
          </section>

          {/* How We Share Your Information */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              How We Share Your Information
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed space-y-3">
              <li><strong className="text-white">Service Providers:</strong> With trusted third-party service providers who assist us in operating our website, conducting our business, or servicing you (e.g., email service providers, analytics providers)</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law, subpoena, or other legal process</li>
              <li><strong className="text-white">Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition</li>
              <li><strong className="text-white">With Your Consent:</strong> When you explicitly consent to the sharing of your information</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              Third-Party Services
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              Our website may contain links to third-party websites, products, or services, including:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed mb-6 space-y-2">
              <li>Music streaming platforms (Apple Music, YouTube Music, Amazon Music)</li>
              <li>Social media platforms</li>
              <li>Book retailers</li>
              <li>Ticket vendors</li>
            </ul>
            <p className="text-white/80 leading-relaxed">
              These third-party sites have their own privacy policies. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services. We encourage you to review their privacy policies before providing any personal information.
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              Cookies and Tracking Technologies
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              We may use cookies and similar tracking technologies to track activity on our website and hold certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
            </p>
            <p className="text-white/80 leading-relaxed mb-6">
              We use cookies for the following purposes:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed mb-6 space-y-2">
              <li>To remember your preferences</li>
              <li>To understand how you use our website</li>
              <li>To improve website performance</li>
              <li>To provide analytics</li>
            </ul>
            <p className="text-white/80 leading-relaxed">
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              Data Security
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed mb-6 space-y-2">
              <li>Encryption of data in transit using SSL/TLS</li>
              <li>Secure servers and databases</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
            </ul>
            <p className="text-white/80 leading-relaxed">
              However, please note that no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              Data Retention
            </h2>
            <p className="text-white/80 leading-relaxed">
              We will retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your personal information, we will securely delete or anonymize it.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              Your Privacy Rights
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed space-y-3">
              <li><strong className="text-white">Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete personal information</li>
              <li><strong className="text-white">Deletion:</strong> Request deletion of your personal information</li>
              <li><strong className="text-white">Opt-Out:</strong> Unsubscribe from marketing emails at any time by clicking the "unsubscribe" link in any email we send</li>
              <li><strong className="text-white">Data Portability:</strong> Request a copy of your personal information in a structured, commonly used format</li>
              <li><strong className="text-white">Objection:</strong> Object to the processing of your personal information</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-6">
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              Children's Privacy
            </h2>
            <p className="text-white/80 leading-relaxed">
              Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will take steps to delete such information.
            </p>
          </section>

          {/* California Privacy Rights */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              California Privacy Rights (CCPA)
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed space-y-2">
              <li>Right to know what personal information is collected, used, shared, or sold</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
              <li>Right to non-discrimination for exercising your CCPA rights</li>
            </ul>
          </section>

          {/* GDPR Rights */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              European Privacy Rights (GDPR)
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              If you are located in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR), including:
            </p>
            <ul className="list-disc list-inside text-white/80 leading-relaxed space-y-2">
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent at any time</li>
            </ul>
          </section>

          {/* Changes to Privacy Policy */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              Changes to This Privacy Policy
            </h2>
            <p className="text-white/80 leading-relaxed">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this policy. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
          </section>

          {/* Contact Us */}
          <section className="mb-12">
            <h2 className="text-3xl font-light mb-6 text-white border-b border-white/10 pb-4">
              Contact Us
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-white/5 border border-white/10 p-6 rounded-sm">
              <p className="text-white/80 mb-2">
                <strong className="text-white">Infinite Reyn Productions</strong>
              </p>
              <p className="text-white/80 mb-2">
                Email: <a href="mailto:privacy@infinitereyn.com" className="text-gold hover:text-gold/80 transition-colors">privacy@infinitereyn.com</a>
              </p>
              <p className="text-white/80">
                Contact Form: <a href="/contact" className="text-gold hover:text-gold/80 transition-colors">infinitereyn.com/contact</a>
              </p>
            </div>
          </section>

          {/* Consent */}
          <section className="mb-12 bg-gold/10 border border-gold/20 p-8 rounded-sm">
            <p className="text-white/90 leading-relaxed text-center">
              By using our website, you consent to our Privacy Policy and agree to its terms.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
